import { useMutation } from '@apollo/client';
import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type {
  InsertChildIndicatorInput,
  InsertIndicatorMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertIndicatorDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  type InsertIndicatorTRPCInput,
  useInsertIndicatorTRPC,
} from './useInsertIndicatorTRPC';

export const useInsertIndicator = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertIndicatorGraphQL, graphqlState] = useMutation(
    InsertIndicatorDocument,
    {
      update: (cache) => {
        evictField(cache, 'control');
        evictField(cache, 'risk');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertIndicatorTRPC();

  const insertIndicator = async (
    variables: InsertChildIndicatorInput
  ): Promise<InsertIndicatorMutation> => {
    if (trpcEnabled) {
      const { schedule, ...rest } = variables;

      // GraphQL codegen makes Title/Type/ParentId optional (InputMaybe),
      // but callers always provide them — cast accordingly
      return trpcMutation.insertIndicator({
        ...rest,
        ...mapScheduleToTRPC(schedule),
      } as InsertIndicatorTRPCInput);
    }

    const result = await insertIndicatorGraphQL({
      variables: { object: variables },
    });
    if (!result.data) {
      throw new Error('Failed to insert indicator');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertIndicator,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertIndicator,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
